import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import FeedbackPanel from './FeedbackPanel';

const ACCENT_COLOR = '#1e293b';

export default function FeedbackPage({ distributeurs }) {
  const [selectedDistributeurId, setSelectedDistributeurId] = useState(
    distributeurs.length > 0 ? distributeurs[0].id : null
  );
  const [onlineStatus, setOnlineStatus] = useState({});
  const [arrowLeft, setArrowLeft] = useState(null);
  const rowRef = useRef(null);
  const cardRefs = useRef({});

  const updateArrowPosition = useCallback(() => {
    const rowEl = rowRef.current;
    const cardEl = cardRefs.current[selectedDistributeurId];
    if (!rowEl || !cardEl) return;
    const rowRect = rowEl.getBoundingClientRect();
    const cardRect = cardEl.getBoundingClientRect();
    setArrowLeft(cardRect.left - rowRect.left + cardRect.width / 2);
  }, [selectedDistributeurId]);

  useLayoutEffect(() => {
    updateArrowPosition();
  }, [updateArrowPosition, distributeurs]);

  useEffect(() => {
    const rowEl = rowRef.current;
    window.addEventListener('resize', updateArrowPosition);
    rowEl?.addEventListener('scroll', updateArrowPosition);
    return () => {
      window.removeEventListener('resize', updateArrowPosition);
      rowEl?.removeEventListener('scroll', updateArrowPosition);
    };
  }, [updateArrowPosition]);

  // Monitor online status based on heartbeats
  useEffect(() => {
    if (!distributeurs || distributeurs.length === 0) return;

    const checkOnlineStatus = async () => {
      const statusMap = {};

      for (const dist of distributeurs) {
        const { data } = await supabase
          .from('heartbeats')
          .select('timestamp')
          .eq('distributeur_id', dist.id)
          .order('timestamp', { ascending: false })
          .limit(1);

        if (data && data.length > 0) {
          const secondsAgo = (Date.now() - new Date(data[0].timestamp)) / 1000;
          statusMap[dist.id] = secondsAgo < 90; // Online if heartbeat less than 90s old
        } else {
          statusMap[dist.id] = false;
        }
      }

      setOnlineStatus(statusMap);
    };

    checkOnlineStatus();

    // Check status every 10 seconds
    const interval = setInterval(checkOnlineStatus, 10000);

    // Subscribe to new heartbeats
    const channel = supabase
      .channel('feedback-heartbeats')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'heartbeats'
      }, () => {
        checkOnlineStatus();
      })
      .subscribe();

    return () => {
      clearInterval(interval);
      supabase.removeChannel(channel);
    };
  }, [distributeurs]);

  const selectedDistributeur = distributeurs.find((d) => d.id === selectedDistributeurId);

  const distributeurCardStyle = (isSelected) => ({
    background: isSelected ? ACCENT_COLOR : '#f8fafc',
    color: isSelected ? '#fff' : '#1e293b',
    borderRadius: 12,
    padding: '1rem 1.5rem',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: isSelected ? `2px solid ${ACCENT_COLOR}` : '2px solid #e2e8f0',
    boxShadow: isSelected ? '0 4px 12px rgba(30, 41, 59, 0.15)' : '0 2px 6px rgba(0,0,0,0.04)',
    minWidth: '180px',
    textAlign: 'center',
  });

  const statusDotStyle = (status) => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: status === 'online' ? '#22c55e' : '#ef4444',
    display: 'inline-block',
    marginRight: 6,
  });

  if (!distributeurs || distributeurs.length === 0) {
    return (
      <div style={{
        background: '#fff',
        borderRadius: 16,
        padding: '2rem',
        textAlign: 'center',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
      }}>
        <p style={{ color: '#94a3b8', fontSize: '0.9rem', margin: 0 }}>
          Aucun distributeur disponible
        </p>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{
        color: '#1e293b',
        marginBottom: '1.5rem',
        fontSize: '1.2rem',
        fontWeight: 700
      }}>
        Feedback
      </h2>

      {/* Distributor selector row */}
      <div ref={rowRef} style={{
        display: 'flex',
        gap: '1rem',
        overflowX: 'auto',
        paddingBottom: '0.5rem'
      }}>
        {distributeurs.map((dist) => (
          <div
            key={dist.id}
            ref={(el) => { cardRefs.current[dist.id] = el; }}
            onClick={() => setSelectedDistributeurId(dist.id)}
            style={distributeurCardStyle(selectedDistributeurId === dist.id)}
            onMouseEnter={(e) => {
              if (selectedDistributeurId !== dist.id) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.08)';
              }
            }}
            onMouseLeave={(e) => {
              if (selectedDistributeurId !== dist.id) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.04)';
              }
            }}
          >
            <div style={{
              fontSize: '0.95rem',
              fontWeight: 700,
              marginBottom: '0.5rem'
            }}>
              {dist.display_name || dist.id}
            </div>
            <div style={{
              fontSize: '0.75rem',
              opacity: 0.8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <span style={statusDotStyle(onlineStatus[dist.id] ? 'online' : 'offline')} />
              {onlineStatus[dist.id] ? 'En ligne' : 'Hors ligne'}
            </div>
          </div>
        ))}
      </div>

      {/* Connector linking the selected card to the panel below */}
      {arrowLeft !== null && (
        <div style={{ position: 'relative', height: '0.75rem' }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: arrowLeft,
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '9px solid transparent',
            borderRight: '9px solid transparent',
            borderTop: `9px solid ${ACCENT_COLOR}`,
          }} />
        </div>
      )}

      {/* Feedback panel for selected distributor */}
      {selectedDistributeurId && (
        <FeedbackPanel
          distributeurId={selectedDistributeurId}
          distributeurName={selectedDistributeur?.display_name || selectedDistributeurId}
        />
      )}
    </div>
  );
}
